/*
  pp-variant-media.js

  Swaps the product gallery image when a variant pill (e.g. "Pack size") is
  clicked, by matching the option value against each gallery image's ALT TEXT
  and, failing that, its FILENAME.

  Why not just assign variant images in Shopify? Because Whatnot collapses a
  variant's gallery to a single image the moment `variant.featured_media` is
  set, and it caches that per variant id, permanently. This does the swap in
  the browser only, so `variant.featured_media` stays null and Whatnot keeps
  showing the full gallery.

  What it looks for (case and punctuation ignored, whole words only):
    option "3 Pack"  -> alt or filename containing "3 pack", "3 pk",
                        "3 count", "3 ct" or "pack of 3"
    option "Single"  -> "single", "1 pack", "1 pk" or "pack of 1"
  So `pig-ears-3-pack.png` matches on its filename alone, and an alt of
  "Beef Tripe 10 Inch 5 Pack" matches the "5 Pack" option.

  No match for any option value = nothing happens and the gallery behaves
  exactly as stock Dawn. Safe to leave installed on every product.
*/

(function () {
  // Space-pad and strip punctuation so `includes()` gives whole-word matching:
  // " 3 pack " will not match an alt of "a 13 pack of ears".
  function normalize(value) {
    return ` ${String(value || '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, ' ')
      .trim()} `;
  }

  // "…/files/1/pig-ears-3-pack.png?v=17&width=493" -> " pig ears 3 pack png "
  function normalizeFilename(src) {
    if (!src) return ' ';
    return normalize(src.split('?')[0].split('/').pop());
  }

  // The strings an alt or filename may use to mean this option value.
  function needlesFor(value) {
    const normalized = normalize(value);
    const needles = [normalized];
    const packMatch = normalized.match(/^ (\d+) (pack|packs|pk|pks|count|ct) $/);

    if (packMatch) {
      const count = packMatch[1];
      needles.push(
        ` ${count} pack `,
        ` ${count} packs `,
        ` ${count} pk `,
        ` ${count} count `,
        ` ${count} ct `,
        ` pack of ${count} `,
        // Unspaced forms. normalize() turns "CowCheek-Hickory-3pk-Large.png"
        // into the single token "3pk", so a needle of " 3 pk " never matches
        // it. Real filenames use both conventions, so both are listed.
        ` ${count}pack `,
        ` ${count}packs `,
        ` ${count}pk `,
        ` ${count}pks `,
        ` ${count}count `,
        ` ${count}ct `
      );
    }

    if (normalized === ' single ' || normalized === ' 1 pack ') {
      needles.push(' single ', ' singles ', ' 1 pack ', ' 1 pk ', ' pack of 1 ');
    }

    return Array.from(new Set(needles));
  }

  // Gallery images paired with the media id `setActiveMedia` expects.
  // Thumbnails are the better source: Dawn passes `alt:` explicitly there,
  // while the large viewer image is rendered from a URL and can come out
  // with an empty alt.
  function galleryEntries(gallery) {
    const thumbnails = gallery.querySelectorAll('[data-target]');
    const nodes = thumbnails.length ? thumbnails : gallery.querySelectorAll('li[data-media-id]');

    return Array.from(nodes)
      .map((node) => {
        const image = node.querySelector('img');
        return {
          mediaId: node.dataset.target || node.dataset.mediaId,
          alt: normalize(image?.getAttribute('alt')),
          file: normalizeFilename(image?.currentSrc || image?.getAttribute('src')),
        };
      })
      .filter((entry) => entry.mediaId);
  }

  // Alt text is the stronger signal, so every image is checked on alt before
  // any image is checked on filename.
  function findMediaIdFor(entries, variant) {
    const values = Array.isArray(variant.options) ? variant.options : [];

    for (let i = 0; i < values.length; i++) {
      const needles = needlesFor(values[i]);

      for (const field of ['alt', 'file']) {
        const match = entries.find((entry) => needles.some((needle) => entry[field].includes(needle)));
        if (match) return match.mediaId;
      }
    }

    return null;
  }

  // Remembers whether we moved this gallery off its default image, so that
  // selecting a variant with no matching photo returns to the first image
  // instead of leaving the previous variant's photo showing.
  const swapped = new WeakMap();

  function applyVariantMedia(productInfo, variant) {
    // A variant with its own image is Shopify's job — Dawn already swaps it.
    if (!productInfo || !variant || variant.featured_media) return;

    const gallery = productInfo.querySelector('media-gallery');
    if (!gallery || typeof gallery.setActiveMedia !== 'function') return;

    const entries = galleryEntries(gallery);
    if (entries.length < 2) return;

    const mediaId = findMediaIdFor(entries, variant);

    if (!mediaId) {
      if (swapped.get(gallery)) {
        // prepend: false keeps the gallery in its authored order
        gallery.setActiveMedia(entries[0].mediaId, false);
        swapped.set(gallery, false);
      }
      return;
    }

    if (gallery.querySelector(`li[data-media-id="${mediaId}"].is-active`)) {
      swapped.set(gallery, true);
      return;
    }

    gallery.setActiveMedia(mediaId, false);
    swapped.set(gallery, true);
  }

  function selectedVariant(productInfo) {
    const json = productInfo.querySelector('variant-selects [data-selected-variant]');
    if (!json) return null;

    try {
      return JSON.parse(json.textContent);
    } catch (error) {
      return null;
    }
  }

  function init() {
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

    subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
      const sectionId = event?.data?.sectionId;
      if (!sectionId) return;

      applyVariantMedia(document.querySelector(`product-info[data-section="${sectionId}"]`), event.data.variant);
    });

    // Landing on ?variant=… should show that variant's photo too.
    document.querySelectorAll('product-info').forEach((productInfo) => {
      applyVariantMedia(productInfo, selectedVariant(productInfo));
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
